import React, { useEffect, useState } from 'react';
import { ResourceCatalogueItem, TaskInput, MaterialInput } from '../types/quote';
import { getResourceCatalogue, createResourceCatalogueItem } from '../api/resourceCatalogue';

interface QuoteLineItemsEditorProps {
  tasks: TaskInput[];
  materials: MaterialInput[];
  isVatRegistered: boolean;
  vatRate: number;
  onTasksChange: (tasks: TaskInput[]) => void;
  onMaterialsChange: (materials: MaterialInput[]) => void;
  onVatChange: (isVatRegistered: boolean, vatRate: number) => void;
}

// The Labour/Materials/VAT editing UI, extracted from QuoteBuilder so it can be embedded both
// there (editing an already-persisted Quote) and in CreateDirectJobPage (building a Quote before
// any Quote/Job exists yet) - fully controlled, no dependency on a persisted quoteId.
const QuoteLineItemsEditor: React.FC<QuoteLineItemsEditorProps> = ({
  tasks,
  materials,
  isVatRegistered,
  vatRate,
  onTasksChange,
  onMaterialsChange,
  onVatChange,
}) => {
  const [catalogue, setCatalogue] = useState<ResourceCatalogueItem[]>([]);

  useEffect(() => {
    getResourceCatalogue().then(setCatalogue).catch(() => setCatalogue([]));
  }, []);

  const labourSubtotal = tasks.reduce(
    (sum, t) => sum + t.resources.reduce((tSum, r) => tSum + r.ratePerHour * r.hours, 0),
    0
  );
  const materialsSubtotal = materials.reduce((sum, m) => sum + m.quantity * m.pricePerUnit, 0);
  const subtotal = labourSubtotal + materialsSubtotal;
  const vatAmount = isVatRegistered ? Math.round((subtotal * vatRate) / 100 * 100) / 100 : 0;
  const total = subtotal + vatAmount;

  const addTask = () => onTasksChange([...tasks, { description: '', resources: [{ description: '', ratePerHour: 0, hours: 0 }] }]);
  const removeTask = (idx: number) => onTasksChange(tasks.filter((_, i) => i !== idx));
  const updateTaskDescription = (idx: number, value: string) =>
    onTasksChange(tasks.map((t, i) => (i === idx ? { ...t, description: value } : t)));

  const addResource = (taskIdx: number) =>
    onTasksChange(
      tasks.map((t, i) => (i === taskIdx ? { ...t, resources: [...t.resources, { description: '', ratePerHour: 0, hours: 0 }] } : t))
    );
  const removeResource = (taskIdx: number, resIdx: number) =>
    onTasksChange(tasks.map((t, i) => (i === taskIdx ? { ...t, resources: t.resources.filter((_, ri) => ri !== resIdx) } : t)));
  const updateResource = (taskIdx: number, resIdx: number, field: 'description' | 'ratePerHour' | 'hours', value: string) =>
    onTasksChange(
      tasks.map((t, i) =>
        i === taskIdx
          ? {
              ...t,
              resources: t.resources.map((r, ri) =>
                ri === resIdx
                  ? {
                      ...r,
                      [field]: field === 'description' ? value : parseFloat(value) || 0,
                      ...(field !== 'description' ? { resourceCatalogueItemId: undefined } : {}),
                    }
                  : r
              ),
            }
          : t
      )
    );
  const applyCatalogueItem = (taskIdx: number, resIdx: number, catalogueItemId: number) => {
    const item = catalogue.find((c) => c.id === catalogueItemId);
    if (!item) return;
    onTasksChange(
      tasks.map((t, i) =>
        i === taskIdx
          ? {
              ...t,
              resources: t.resources.map((r, ri) =>
                ri === resIdx ? { ...r, description: item.description, ratePerHour: item.ratePerHour, resourceCatalogueItemId: item.id } : r
              ),
            }
          : t
      )
    );
  };
  const saveResourceToCatalogue = async (taskIdx: number, resIdx: number) => {
    const resource = tasks[taskIdx]?.resources[resIdx];
    if (!resource || !resource.description) return;
    try {
      const created = await createResourceCatalogueItem(resource.description, resource.ratePerHour);
      setCatalogue((prev) => [...prev, created]);
    } catch {
      // Non-critical - the line item is unaffected either way.
    }
  };

  const addMaterial = () => onMaterialsChange([...materials, { description: '', quantity: 1, pricePerUnit: 0 }]);
  const removeMaterial = (idx: number) => onMaterialsChange(materials.filter((_, i) => i !== idx));
  const updateMaterial = (idx: number, field: 'description' | 'quantity' | 'pricePerUnit', value: string) =>
    onMaterialsChange(materials.map((m, i) => (i === idx ? { ...m, [field]: field === 'description' ? value : parseFloat(value) || 0 } : m)));

  const currency = (n: number) => `R${n.toFixed(2)}`;

  return (
    <div className="space-y-4">
      {/* Labour section */}
      <div className="space-y-3">
        <h4 className="text-label-lg font-label-bold text-on-surface-variant">Labour</h4>
        {tasks.map((task, taskIdx) => (
          <div key={taskIdx} className="p-3 rounded-lg bg-surface border border-outline-variant space-y-2">
            <div className="flex items-center gap-2">
              <input
                className="flex-grow bg-transparent border-b border-outline-variant text-sm py-1 focus:outline-none focus:border-primary"
                placeholder={`Task ${taskIdx + 1} - e.g. Remove window`}
                value={task.description}
                onChange={(e) => updateTaskDescription(taskIdx, e.target.value)}
              />
              <button type="button" onClick={() => removeTask(taskIdx)} className="text-outline hover:text-error text-xs">Remove task</button>
            </div>

            {task.resources.map((resource, resIdx) => (
              <div key={resIdx} className="flex flex-wrap items-center gap-2 pl-3 border-l-2 border-outline-variant">
                <input
                  className="flex-grow min-w-[120px] bg-transparent border-b border-outline-variant text-sm py-1 focus:outline-none focus:border-primary"
                  placeholder="Resource description"
                  value={resource.description}
                  onChange={(e) => updateResource(taskIdx, resIdx, 'description', e.target.value)}
                />
                {catalogue.length > 0 && (
                  <select
                    className="text-xs bg-surface-container-low border border-outline-variant rounded px-1 py-1"
                    value={resource.resourceCatalogueItemId ?? ''}
                    onChange={(e) => e.target.value && applyCatalogueItem(taskIdx, resIdx, parseInt(e.target.value))}
                  >
                    <option value="">Rate card...</option>
                    {catalogue.map((c) => (
                      <option key={c.id} value={c.id}>{c.description} (R{c.ratePerHour}/hr)</option>
                    ))}
                  </select>
                )}
                <input
                  type="number"
                  className="w-20 bg-transparent border-b border-outline-variant text-sm py-1 text-right focus:outline-none focus:border-primary"
                  placeholder="Rate/hr"
                  value={resource.ratePerHour || ''}
                  onChange={(e) => updateResource(taskIdx, resIdx, 'ratePerHour', e.target.value)}
                />
                <span className="text-xs text-outline">x</span>
                <input
                  type="number"
                  className="w-16 bg-transparent border-b border-outline-variant text-sm py-1 text-right focus:outline-none focus:border-primary"
                  placeholder="Hours"
                  value={resource.hours || ''}
                  onChange={(e) => updateResource(taskIdx, resIdx, 'hours', e.target.value)}
                />
                <span className="text-xs text-on-surface-variant w-20 text-right">{currency(resource.ratePerHour * resource.hours)}</span>
                <button type="button" onClick={() => saveResourceToCatalogue(taskIdx, resIdx)} title="Save to rate card" className="text-outline hover:text-primary text-xs">Save</button>
                <button type="button" onClick={() => removeResource(taskIdx, resIdx)} className="text-outline hover:text-error text-xs">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => addResource(taskIdx)} className="text-xs text-secondary hover:underline pl-3">+ Add resource</button>
          </div>
        ))}
        <button type="button" onClick={addTask} className="text-sm text-primary hover:underline">+ Add task</button>
      </div>

      {/* Materials section */}
      <div className="space-y-2">
        <h4 className="text-label-lg font-label-bold text-on-surface-variant">Materials</h4>
        {materials.map((material, idx) => (
          <div key={idx} className="flex flex-wrap items-center gap-2">
            <input
              className="flex-grow min-w-[120px] bg-transparent border-b border-outline-variant text-sm py-1 focus:outline-none focus:border-primary"
              placeholder="Material description"
              value={material.description}
              onChange={(e) => updateMaterial(idx, 'description', e.target.value)}
            />
            <input
              type="number"
              className="w-16 bg-transparent border-b border-outline-variant text-sm py-1 text-right focus:outline-none focus:border-primary"
              placeholder="Qty"
              value={material.quantity || ''}
              onChange={(e) => updateMaterial(idx, 'quantity', e.target.value)}
            />
            <span className="text-xs text-outline">x</span>
            <input
              type="number"
              className="w-20 bg-transparent border-b border-outline-variant text-sm py-1 text-right focus:outline-none focus:border-primary"
              placeholder="Price/unit"
              value={material.pricePerUnit || ''}
              onChange={(e) => updateMaterial(idx, 'pricePerUnit', e.target.value)}
            />
            <span className="text-xs text-on-surface-variant w-20 text-right">{currency(material.quantity * material.pricePerUnit)}</span>
            <button type="button" onClick={() => removeMaterial(idx)} className="text-outline hover:text-error text-xs">✕</button>
          </div>
        ))}
        <button type="button" onClick={addMaterial} className="text-sm text-primary hover:underline">+ Add material</button>
      </div>

      {/* VAT + totals */}
      <div className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          id="vat-toggle"
          checked={isVatRegistered}
          onChange={(e) => onVatChange(e.target.checked, vatRate)}
        />
        <label htmlFor="vat-toggle" className="text-on-surface-variant">VAT registered</label>
        {isVatRegistered && (
          <>
            <input
              type="number"
              className="w-14 bg-transparent border-b border-outline-variant text-sm py-1 text-right focus:outline-none focus:border-primary"
              value={vatRate}
              onChange={(e) => onVatChange(isVatRegistered, parseFloat(e.target.value) || 0)}
            />
            <span className="text-outline">%</span>
          </>
        )}
      </div>

      <div className="pt-2 border-t border-outline-variant text-sm space-y-1">
        <div className="flex justify-between text-on-surface-variant"><span>Labour subtotal</span><span>{currency(labourSubtotal)}</span></div>
        <div className="flex justify-between text-on-surface-variant"><span>Materials subtotal</span><span>{currency(materialsSubtotal)}</span></div>
        {isVatRegistered && <div className="flex justify-between text-on-surface-variant"><span>VAT ({vatRate}%)</span><span>{currency(vatAmount)}</span></div>}
        <div className="flex justify-between font-label-bold text-on-surface text-base"><span>Total</span><span>{currency(total)}</span></div>
      </div>
    </div>
  );
};

export default QuoteLineItemsEditor;
